/**
 * AutoGlow · Founder Advisor Dashboard · Sprint T-13.
 *
 * Renders 10 widgets that exclusively CONSUME intelligence from AG-06
 * Marketplace endpoints. NO business logic in React. NO data calculations.
 * Tabs persisted in URL via `?tab=` query param.
 *
 * Widgets:
 *   F1  EcosystemSnapshot         /api/ai/marketplace/ecosystem_overview
 *   F2  CoverageBreadthMap        derived from F1
 *   F3  KPIGrid                   derived from F1
 *   F4  SectorIntelligenceView    /api/ai/marketplace/sector_intelligence
 *   F5  GeoIntelligenceView       /api/ai/marketplace/geo_intelligence
 *   F6  LeaderboardTable          /api/ai/marketplace/leaderboard
 *   F7  DemandSupplyMatrix        /api/ai/marketplace/demand_supply_gap
 *   F8  TrendsTimeSeries          /api/ai/marketplace/trends
 *   F9  TopPerformersList         /api/ai/marketplace/leaderboard (alias)
 *   F10 SectorVelocityList        /api/ai/marketplace/trends (alias)
 */
import React, { useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, RefreshCw } from "lucide-react";

import {
  QueryGate, WidgetCard, WidgetGrid,
  MetricCard, BreadthGauge, RankedList,
  LeaderboardTable, HeatMap, TrendChart,
  CitationBadge,
} from "../../components/advisor";
import {
  useEcosystemOverview, useSectorIntelligence, useGeoIntelligence,
  useLeaderboard, useDemandSupplyGap, useTrends,
} from "../../hooks/useAdvisor";

const FOUNDER_TABS = [
  { id: "overview", label: "Overview" },
  { id: "sectors", label: "Sectores" },
  { id: "geo", label: "Geo" },
  { id: "gaps", label: "Demanda/Oferta" },
  { id: "trends", label: "Tendencias" },
  { id: "leaderboard", label: "Leaderboard" },
];

const LEADERBOARD_METRICS = [
  { id: "funnel_health", label: "Funnel Health" },
  { id: "business_score", label: "Business Score" },
  { id: "catalog_count", label: "Catálogo" },
  { id: "rating_avg", label: "Rating ★" },
  { id: "rating_count", label: "# Reseñas" },
  { id: "taxonomy_codes_count", label: "Cobertura códigos" },
];

const TREND_HORIZONS = [7, 14, 30, 60, 90];

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function FounderHeader({ activeTab, onChangeTab, ecosystemQuery }) {
  return (
    <header className="sticky top-0 z-20 border-b border-[var(--ag-border)] bg-[var(--ag-bg)]/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3">
        <Link
          to="/founder"
          data-testid="founder-advisor-back"
          className="flex items-center gap-1 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Founder
        </Link>
        <div className="flex-1">
          <h1 className="text-lg font-semibold tracking-tight" data-testid="founder-advisor-title">
            Advisor · Founder Dashboard
          </h1>
          <p className="text-[11px] text-[var(--ag-text-dim)]">
            Visión ecosistémica · datos AG-06 · solo lectura · sprint T-13
          </p>
        </div>
        <button
          data-testid="founder-advisor-refresh"
          type="button"
          onClick={() => ecosystemQuery.refetch()}
          className="flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          aria-label="Refrescar inteligencia"
        >
          <RefreshCw className={`h-3 w-3 ${ecosystemQuery.isFetching ? "animate-spin" : ""}`} />
          Refrescar
        </button>
      </div>
      <nav className="border-t border-[var(--ag-border)] bg-[var(--ag-surface)]/40">
        <div className="mx-auto flex max-w-7xl flex-wrap gap-1 overflow-x-auto px-4 py-1.5">
          {FOUNDER_TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`founder-advisor-tab-${t.id}`}
              type="button"
              onClick={() => onChangeTab(t.id)}
              className={`shrink-0 rounded px-3 py-1 text-xs ${
                activeTab === t.id
                  ? "bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "text-[var(--ag-text-dim)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Overview tab · F1 + F2 + F3
// ---------------------------------------------------------------------------

function OverviewTab({ ecosystemQuery }) {
  return (
    <section className="space-y-4">
      <QueryGate query={ecosystemQuery} testId="founder-ecosystem">
        {(data) => {
          const t = data.totals || {};
          const a = data.averages || {};
          const d = data.deciles || {};
          const kpis = [
            { label: "Organizaciones", value: t.organizations, hint: "total ecosistema" },
            { label: "Activas", value: t.active_organizations },
            { label: "Públicas", value: t.public_organizations },
            { label: "Con catálogo", value: t.with_catalog },
            { label: "Con reseñas", value: t.with_reviews },
            { label: "Con GMB", value: t.with_google_place_id },
            { label: "Funnel medio", value: a.avg_funnel_health, hint: "0-100" },
            { label: "Business medio", value: a.avg_business_score, hint: "0-100" },
          ];
          return (
            <>
              <WidgetCard
                title="Ecosystem Snapshot"
                description={`Snapshot ${data.snapshot_at?.slice(0, 10) || ""} · N=${t.organizations}`}
                citations={data.grounding_sources}
                testId="widget-F1-ecosystem-snapshot"
              >
                <WidgetGrid cols="lg:grid-cols-4">
                  {kpis.map((k) => (
                    <MetricCard
                      key={k.label}
                      label={k.label}
                      value={k.value}
                      hint={k.hint}
                      testId={`founder-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  ))}
                </WidgetGrid>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded border border-[var(--ag-border)] p-3 text-xs">
                    <div className="text-[var(--ag-text-dim)]">Deciles · Funnel Health</div>
                    <div className="mt-1 flex justify-between font-mono">
                      <span>p10: {d.funnel_health_p10 ?? "—"}</span>
                      <span>p50: {d.funnel_health_p50 ?? "—"}</span>
                      <span>p90: {d.funnel_health_p90 ?? "—"}</span>
                    </div>
                  </div>
                  <div className="rounded border border-[var(--ag-border)] p-3 text-xs">
                    <div className="text-[var(--ag-text-dim)]">Deciles · Business Score</div>
                    <div className="mt-1 flex justify-between font-mono">
                      <span>p10: {d.business_score_p10 ?? "—"}</span>
                      <span>p50: {d.business_score_p50 ?? "—"}</span>
                      <span>p90: {d.business_score_p90 ?? "—"}</span>
                    </div>
                  </div>
                  <div className="rounded border border-[var(--ag-border)] p-3 text-xs">
                    <div className="text-[var(--ag-text-dim)]">Catálogo & reseñas</div>
                    <div className="mt-1 flex justify-between font-mono">
                      <span>{t.catalog_items_total ?? 0} items</span>
                      <span>{t.reviews_total ?? 0} reseñas</span>
                    </div>
                  </div>
                </div>
              </WidgetCard>

              <WidgetCard
                title="Coverage Breadth"
                description="% de códigos taxonómicos con ≥1 organización"
                citations={data.grounding_sources}
                testId="widget-F2-coverage-breadth"
              >
                <BreadthGauge
                  covered={t.taxonomy_codes_covered}
                  total={t.taxonomy_codes_total}
                  label="Códigos taxonómicos cubiertos"
                  testId="founder-breadth"
                />
              </WidgetCard>

              <WidgetGrid cols="lg:grid-cols-2">
                <WidgetCard
                  title="Top 5 códigos por organizaciones"
                  citations={["AG-06"]}
                  testId="widget-F3a-top-codes"
                >
                  <RankedList items={data.top_5_codes_by_org_count} renderRight={(it) => `${it.orgs} orgs`} />
                </WidgetCard>
                <WidgetCard
                  title="Top 5 ciudades por organizaciones"
                  citations={["AG-06"]}
                  testId="widget-F3b-top-cities"
                >
                  <RankedList items={data.top_5_cities} renderRight={(it) => `${it.orgs} orgs`} />
                </WidgetCard>
              </WidgetGrid>
            </>
          );
        }}
      </QueryGate>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sectors tab · F4
// ---------------------------------------------------------------------------

function SectorsTab({ ecosystemQuery, sectorCode, onChangeSector }) {
  const intel = useSectorIntelligence(sectorCode);
  const sectorOptions = useMemo(() => {
    const items = ecosystemQuery.data?.top_5_codes_by_org_count || [];
    return items.map((i) => ({ code: i.code, label: i.code }));
  }, [ecosystemQuery.data]);

  return (
    <section className="space-y-4">
      <WidgetCard
        title="Selección de sector"
        description="Pulsa un código para activar el drill-down sectorial"
        testId="widget-F4-sector-picker"
      >
        <div className="flex flex-wrap gap-1.5">
          {sectorOptions.map((opt) => (
            <button
              key={opt.code}
              data-testid={`founder-sector-pick-${opt.code}`}
              type="button"
              onClick={() => onChangeSector(opt.code)}
              className={`rounded border px-2 py-1 text-xs ${
                sectorCode === opt.code
                  ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {opt.code}
            </button>
          ))}
          {sectorOptions.length === 0 && (
            <span className="text-xs text-[var(--ag-text-dim)]">Cargando códigos del ecosistema…</span>
          )}
        </div>
      </WidgetCard>

      {sectorCode && (
        <QueryGate query={intel} testId="founder-sector-intel">
          {(data) => {
            const t = data.totals || {};
            const a = data.averages || {};
            const top = data.top_performers || [];
            return (
              <>
                <WidgetCard
                  title={`Sector Intelligence · ${data.sector_code}`}
                  description={data.node?.name?.es || data.node?.name || ""}
                  citations={data.grounding_sources}
                  testId="widget-F4-sector-intelligence"
                >
                  <WidgetGrid cols="lg:grid-cols-4">
                    <MetricCard label="Orgs en sector" value={t.orgs_in_sector} />
                    <MetricCard label="Activas" value={t.active_orgs} />
                    <MetricCard label="Con catálogo" value={t.with_catalog} />
                    <MetricCard label="Funnel medio" value={a.funnel_health} hint="0-100" />
                  </WidgetGrid>
                </WidgetCard>

                <WidgetGrid cols="lg:grid-cols-2">
                  <WidgetCard title="Sub-códigos" testId="widget-F4b-subcodes" citations={["AG-06"]}>
                    <RankedList items={data.subcode_distribution} renderRight={(it) => `${it.orgs} orgs`} />
                  </WidgetCard>
                  <WidgetCard title="Distribución geográfica" testId="widget-F4c-geo-dist" citations={["AG-06"]}>
                    <RankedList items={data.geo_distribution} renderRight={(it) => `${it.orgs} orgs`} />
                  </WidgetCard>
                </WidgetGrid>

                <WidgetCard
                  title="Top Performers · sector"
                  description="Visible solo para founder · anonimizado para clientes"
                  citations={data.grounding_sources}
                  testId="widget-F9-top-performers"
                >
                  {top.length === 0 ? (
                    <div className="text-xs text-[var(--ag-text-dim)]">Información insuficiente.</div>
                  ) : (
                    <ul className="space-y-1">
                      {top.map((p) => (
                        <li
                          key={p.org_id}
                          data-testid={`top-performer-${p.org_id}`}
                          className="flex items-center justify-between rounded border border-[var(--ag-border)] px-2 py-1.5 text-xs"
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-[var(--ag-text)]">{p.name || p.org_id}</span>
                            <span className="text-[var(--ag-text-dim)]">· {p.city || "—"}</span>
                          </span>
                          <span className="flex items-center gap-3 font-mono text-[var(--ag-text-dim)]">
                            <span>F: {p.funnel_health ?? "—"}</span>
                            <span>B: {p.business_score ?? "—"}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </WidgetCard>
              </>
            );
          }}
        </QueryGate>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Geo tab · F5
// ---------------------------------------------------------------------------

function GeoTab({ ecosystemQuery, city, onChangeCity }) {
  const intel = useGeoIntelligence(city);
  const cityOptions = useMemo(() => {
    const items = ecosystemQuery.data?.top_5_cities || [];
    return items.map((i) => ({ city: i.city, orgs: i.orgs }));
  }, [ecosystemQuery.data]);

  return (
    <section className="space-y-4">
      <WidgetCard
        title="Selección de ciudad"
        description="Pulsa una ciudad para activar el drill-down geográfico"
        testId="widget-F5-city-picker"
      >
        <div className="flex flex-wrap gap-1.5">
          {cityOptions.map((opt) => (
            <button
              key={opt.city}
              data-testid={`founder-city-pick-${opt.city}`}
              type="button"
              onClick={() => onChangeCity(opt.city)}
              className={`rounded border px-2 py-1 text-xs ${
                city === opt.city
                  ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {opt.city} <span className="ml-1 text-[10px] opacity-70">({opt.orgs})</span>
            </button>
          ))}
          {cityOptions.length === 0 && (
            <span className="text-xs text-[var(--ag-text-dim)]">Cargando ciudades del ecosistema…</span>
          )}
        </div>
      </WidgetCard>

      {city && (
        <QueryGate query={intel} testId="founder-geo-intel">
          {(data) => {
            const t = data.totals || {};
            const a = data.averages || {};
            return (
              <>
                <WidgetCard
                  title={`Geo Intelligence · ${data.city}`}
                  citations={data.grounding_sources}
                  testId="widget-F5-geo-intelligence"
                >
                  <WidgetGrid cols="lg:grid-cols-4">
                    <MetricCard label="Orgs en ciudad" value={t.orgs_in_city} />
                    <MetricCard label="Activas" value={t.active_orgs} />
                    <MetricCard label="Con catálogo" value={t.with_catalog} />
                    <MetricCard label="Funnel medio" value={a.funnel_health} hint="0-100" />
                  </WidgetGrid>
                </WidgetCard>

                <WidgetCard title="Códigos servidos en la ciudad" testId="widget-F5b-codes" citations={["AG-06"]}>
                  <RankedList items={data.codes_in_city} renderRight={(it) => `${it.orgs} orgs`} />
                </WidgetCard>
              </>
            );
          }}
        </QueryGate>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Demand/Supply tab · F7
// ---------------------------------------------------------------------------

function GapsTab() {
  const q = useDemandSupplyGap();
  return (
    <QueryGate query={q} testId="founder-gaps">
      {(data) => {
        const gaps = data.gaps || [];
        const byBand = data.by_priority_band || {};
        return (
          <section className="space-y-4">
            <WidgetCard
              title="Demand/Supply Matrix"
              description={`Total gaps detectados: ${data.total_gaps ?? 0}`}
              citations={data.grounding_sources}
              testId="widget-F7-demand-supply"
            >
              <div className="mb-3 flex gap-2 text-xs">
                <span className="rounded bg-red-500/15 px-2 py-0.5 text-red-500">+++ {byBand["+++"] ?? 0}</span>
                <span className="rounded bg-orange-500/15 px-2 py-0.5 text-orange-500">++ {byBand["++"] ?? 0}</span>
                <span className="rounded bg-yellow-500/15 px-2 py-0.5 text-yellow-600">+ {byBand["+"] ?? 0}</span>
              </div>
              <HeatMap cells={gaps} testId="founder-gaps-heatmap" />
              <p className="mt-2 text-[10px] text-[var(--ag-text-dim)]">
                Metodología: demand/supply sin fuentes externas · derivado del propio marketplace.
              </p>
            </WidgetCard>
            <WidgetCard
              title="Gaps priorizados"
              description="Recomendaciones backend · solo lectura"
              citations={["AG-06"]}
              testId="widget-F7b-gap-list"
            >
              <ul className="space-y-1">
                {gaps.slice(0, 20).map((g, i) => (
                  <li
                    key={`${g.code}-${g.city}-${i}`}
                    data-testid={`gap-${g.code}-${g.city}`}
                    className="rounded border border-[var(--ag-border)] px-2 py-1.5 text-xs"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono">{g.code} · {g.city}</span>
                      <span className="rounded bg-[var(--ag-surface-2)] px-1.5 py-0.5 text-[10px]">
                        {g.gap_label}
                      </span>
                    </div>
                    <p className="mt-1 text-[var(--ag-text-dim)]">{g.recommendation}</p>
                  </li>
                ))}
                {gaps.length === 0 && (
                  <li className="text-xs text-[var(--ag-text-dim)]">Información insuficiente.</li>
                )}
              </ul>
            </WidgetCard>
          </section>
        );
      }}
    </QueryGate>
  );
}

// ---------------------------------------------------------------------------
// Trends tab · F8 + F10
// ---------------------------------------------------------------------------

function TrendsTab({ horizon, onChangeHorizon }) {
  const q = useTrends(horizon);
  return (
    <section className="space-y-4">
      <WidgetCard
        title="Horizonte de análisis"
        testId="widget-F8-horizon-picker"
      >
        <div className="flex flex-wrap gap-1.5">
          {TREND_HORIZONS.map((h) => (
            <button
              key={h}
              data-testid={`founder-trend-horizon-${h}`}
              type="button"
              onClick={() => onChangeHorizon(h)}
              className={`rounded border px-2 py-1 text-xs ${
                horizon === h
                  ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {h}d
            </button>
          ))}
        </div>
      </WidgetCard>

      <QueryGate query={q} testId="founder-trends">
        {(data) => {
          const series = (data.series && data.series.new_organizations_per_day) || [];
          return (
            <>
              <WidgetCard
                title="Trends · nuevas organizaciones"
                description={`Total ventana ${data.horizon_days}d: ${data.total_new_orgs_in_window ?? 0}`}
                citations={data.grounding_sources}
                testId="widget-F8-trends-series"
              >
                <TrendChart series={series} testId="founder-trends-chart" />
              </WidgetCard>

              <WidgetCard
                title="Sector velocity"
                description={`Nuevas orgs por sector L1 en ventana ${data.horizon_days}d`}
                citations={data.grounding_sources}
                testId="widget-F10-sector-velocity"
              >
                <RankedList
                  items={(data.sector_velocity || []).map((s) => ({ code: s.code, label: s.velocity_label, value: s.new_orgs_window }))}
                  renderRight={(it) => `${it.value} · ${it.label}`}
                />
              </WidgetCard>
            </>
          );
        }}
      </QueryGate>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard tab · F6
// ---------------------------------------------------------------------------

function LeaderboardTab({ metric, onChangeMetric }) {
  const navigate = useNavigate();
  const q = useLeaderboard(metric, 20);
  return (
    <section className="space-y-4">
      <WidgetCard title="Selección de métrica" testId="widget-F6-metric-picker">
        <div className="flex flex-wrap gap-1.5">
          {LEADERBOARD_METRICS.map((m) => (
            <button
              key={m.id}
              data-testid={`founder-leaderboard-metric-${m.id}`}
              type="button"
              onClick={() => onChangeMetric(m.id)}
              className={`rounded border px-2 py-1 text-xs ${
                metric === m.id
                  ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </WidgetCard>

      <QueryGate query={q} testId="founder-leaderboard">
        {(data) => (
          <WidgetCard
            title={`Leaderboard · ${data.metric}`}
            description={`Top ${data.leaders?.length || 0} sobre ${data.total_orgs_considered ?? 0} orgs · clic en una fila para abrir Client Advisor`}
            citations={data.grounding_sources}
            testId="widget-F6-leaderboard"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--ag-border)] text-[var(--ag-text-dim)]">
                    <th className="px-2 py-1.5 text-left">#</th>
                    <th className="px-2 py-1.5 text-left">Centro</th>
                    <th className="px-2 py-1.5 text-left">Ciudad</th>
                    <th className="px-2 py-1.5 text-right">{data.metric}</th>
                    <th className="px-2 py-1.5 text-right">Funnel</th>
                    <th className="px-2 py-1.5 text-right">Business</th>
                    <th className="px-2 py-1.5 text-right">Abrir</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.leaders || []).map((l) => (
                    <tr
                      key={`${l.org_id}-${l.rank}`}
                      data-testid={`leaderboard-row-${l.rank}`}
                      className="border-b border-[var(--ag-border)]/40 hover:bg-[var(--ag-surface-2)]"
                    >
                      <td className="px-2 py-1.5 font-mono">{l.rank}</td>
                      <td className="px-2 py-1.5">{l.name || "—"}</td>
                      <td className="px-2 py-1.5 text-[var(--ag-text-dim)]">{l.city || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{l.value ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-[var(--ag-text-dim)]">{l.funnel_health ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-[var(--ag-text-dim)]">{l.business_score ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right">
                        {l.slug && (
                          <button
                            data-testid={`leaderboard-open-${l.slug}`}
                            type="button"
                            onClick={() => navigate(`/centro/advisor/${l.slug}`)}
                            className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-1.5 py-0.5 text-[10px] hover:bg-[var(--ag-surface)]"
                          >
                            <ExternalLink className="h-2.5 w-2.5" /> Ver
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </WidgetCard>
        )}
      </QueryGate>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export default function FounderAdvisorDashboard() {
  const [search, setSearch] = useSearchParams();
  const activeTab = search.get("tab") || "overview";

  const setTab = (t) => {
    const next = new URLSearchParams(search);
    next.set("tab", t);
    setSearch(next, { replace: true });
  };
  const setQS = (k, v) => {
    const next = new URLSearchParams(search);
    if (v) next.set(k, v); else next.delete(k);
    setSearch(next, { replace: true });
  };

  const ecosystemQuery = useEcosystemOverview(true);

  const sectorCode = search.get("code") || "";
  const city = search.get("city") || "";
  const horizon = parseInt(search.get("horizon") || "30", 10);
  const metric = search.get("metric") || "funnel_health";

  return (
    <div className="min-h-screen bg-[var(--ag-bg)] text-[var(--ag-text)]" data-testid="founder-advisor-dashboard">
      <FounderHeader activeTab={activeTab} onChangeTab={setTab} ecosystemQuery={ecosystemQuery} />
      <main className="mx-auto max-w-7xl px-4 py-4">
        {activeTab === "overview" && <OverviewTab ecosystemQuery={ecosystemQuery} />}
        {activeTab === "sectors" && (
          <SectorsTab
            ecosystemQuery={ecosystemQuery}
            sectorCode={sectorCode}
            onChangeSector={(c) => setQS("code", c)}
          />
        )}
        {activeTab === "geo" && (
          <GeoTab
            ecosystemQuery={ecosystemQuery}
            city={city}
            onChangeCity={(c) => setQS("city", c)}
          />
        )}
        {activeTab === "gaps" && <GapsTab />}
        {activeTab === "trends" && (
          <TrendsTab horizon={horizon} onChangeHorizon={(h) => setQS("horizon", String(h))} />
        )}
        {activeTab === "leaderboard" && (
          <LeaderboardTab metric={metric} onChangeMetric={(m) => setQS("metric", m)} />
        )}
        <footer className="mt-6 border-t border-[var(--ag-border)] pt-3">
          <CitationBadge sources={["AG-06", "ai_platform_freeze=true", "T-13 frontend · solo lectura"]} testId="founder-advisor-footer" />
        </footer>
      </main>
    </div>
  );
}
